import { useCallback, useEffect, useState } from "react";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import IContact from "@models/IContact";
import IVendorModel from "@models/IVendorModel";
import IAccount_Note from "@models/IAccount_Note";
import { buildAuthHeadersForFetch, buildRestUrlWithQuery } from "@services/RestService";
import RestEndpointsEnum from "@models/RestEndpointsEnum";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { LoadingResults } from "@components/shared/LoadingResults/LoadingResults";
import { NoteEditorModal } from "./NoteEditorModal/NoteEditorModal";
import { OwIconButton } from "@components/shared/OwIconButton/OwIconButton";
import "./NotesViewer.scss";
import { useAppContextNonNull } from "@contexts/AppContext";

type Props = {
    vendorModel: IVendorModel;
    vendorContacts: IContact[];
};

export const NotesViewer = (props: Props) => {
    const { vendorModel, vendorContacts } = props;
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();

    const [notes, setNotes] = useState<IAccount_Note[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const fetchNotes = useCallback(async () => {
        try {
            setIsLoading(true);

            const url = buildRestUrlWithQuery({
                endpoint: RestEndpointsEnum.AccountNotesGetAll,
                query: {
                    vendor_id: vendorModel.id,
                    supplier_id: supplierModel.id,
                    limit: 100,
                    offset: 0,
                },
            });

            const headers = buildAuthHeadersForFetch({
                userModel,
                supplierModel,
                vendorModel,
            });

            console.log(headers);

            const response = await fetch(url, {
                method: "GET",
                headers,
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch notes: ${response.statusText}`);
            }

            const data = (await response.json()) as IAccount_Note[];
            setNotes(data || []);
            console.log(data);
        } catch (error) {
            console.error("Error fetching notes:", error);
            setNotes([]);
        } finally {
            setIsLoading(false);
        }
    }, [vendorModel, supplierModel, userModel]);

    useEffect(() => {
        if (vendorModel && vendorModel.id) {
            fetchNotes();
        }
    }, [vendorModel, fetchNotes]);

    const closeModal = () => {
        uiContext.setModalComponent(null);
        uiContext.showModal(false);
    };

    const handleAddNote = () => {
        const modalComponent = (
            <NoteEditorModal
                vendorModel={vendorModel}
                vendorContacts={vendorContacts}
                closeModalFn={closeModal}
                onSaveSuccess={fetchNotes}
            />
        );

        uiContext.setModalComponent(modalComponent);
        uiContext.showModal(true);
    };

    const handleEditNote = (note: IAccount_Note) => {
        const modalComponent = (
            <NoteEditorModal
                vendorModel={vendorModel}
                vendorContacts={vendorContacts}
                closeModalFn={closeModal}
                onSaveSuccess={fetchNotes}
                existingNote={note}
            />
        );

        uiContext.setModalComponent(modalComponent);
        uiContext.showModal(true);
    };

    const formatDate = (date: string | Date) => {
        if (!date) return "";
        return new Date(date).toLocaleDateString();
    };

    return (
        <div className={"NotesViewer"}>
            <div className={"d-flex align-items-center"}>
                <OwTitle title={"notes"} tag={"h3"} />
                <OwButton title={"add"} leftIconClass={"fas fa-plus"} fn={handleAddNote} className={"ms-2 btn-warning"} />
            </div>

            {isLoading ? (
                <LoadingResults />
            ) : notes.length === 0 ? (
                <div className={"mt-3 text-muted"}>
                    <OwLabel title={"no_notes_found"} />
                </div>
            ) : (
                <>
                    <div className={"mt-2"}>
                        <div className={"row p-1"}>
                            <div className="col-1"></div>
                            <div className="col-5">
                                <OwLabel title={"note"} isBold={true} />
                            </div>
                            <div className="col-3">
                                <OwLabel title={"contact"} isBold={true} />
                            </div>
                            <div className="col-2">
                                <OwLabel title={"date"} isBold={true} />
                            </div>
                            <div className="col-1">
                                <OwLabel title={"user"} isBold={true} />
                            </div>
                        </div>
                    </div>
                    <div className={"notes-container"}>
                        {notes.map((note, index) => {
                            return (
                                <div key={note.id || index} className="note-row row p-1 ">
                                    <div className={"col-1"}>
                                        <OwButton centerIconClass={"fas fa-pencil"} fn={() => handleEditNote(note)} />
                                    </div>
                                    <div className={"col-5"}>
                                        {note.note?.substring(0, 100)}
                                        {note.note?.length > 100 ? "..." : ""}
                                    </div>
                                    <div className={"col-3"}>{note.Contact?.name || note.contact_name || "-"}</div>
                                    <div className={"col-2"}>{formatDate(note.created_at)}</div>
                                    <div className={"col-1"}>{note.User?.name || "-"}</div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};
